"use client";
import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "motion/react";
import { onAuthPrompt, type AuthPromptReason } from "@/lib/authPrompt";
import { signIn } from "@/lib/authClient";
import { clearPendingFavorite } from "@/lib/favorites";
import { useT } from "@/lib/i18n";

// Modale « lazy signup » : montée une seule fois au layout racine, elle s'ouvre
// quand un visiteur non connecté clique sur ♥ (favori) ou « sauver un setup ».
// Google reste dans le flux (au retour, l'autosave du configurateur restaure le montage).
//
// ⚠️ Deux façons de fermer, à ne pas confondre :
//  · RENONCER (croix, fond, Échap) → on oublie le favori mis en attente, sinon il
//    se poserait tout seul à la prochaine connexion, des jours plus tard ;
//  · PARTIR SE CONNECTER (Google, lien email) → l'attente doit survivre, c'est
//    tout l'intérêt : le ♥ cliqué avant la connexion est honoré au retour.

// Tempo. La modale s'ouvre sur un geste ISOLÉ (un ♥ cliqué) : on peut prendre le temps de la
// montrer, là où un menu doit être instantané. Ressort à rebond très faible — une boîte qui
// tressaute fait bon marché — et cascade du contenu pour que l'œil suive.
const VEIL = { duration: 0.3, ease: "easeOut" } as const;
const CARD_IN = { type: "spring", bounce: 0.16, visualDuration: 0.52 } as const;
const ITEM_IN = { duration: 0.42, ease: [0.22, 1, 0.36, 1] } as const;
const step = (i: number) => ({ ...ITEM_IN, delay: 0.14 + i * 0.07 });

const MESSAGES = {
  favorite: {
    title: "Enregistre tes favoris",
    sub: "Crée un compte pour retrouver tes produits favoris sur tous tes appareils.",
  },
  setup: {
    title: "Sauvegarde ton setup",
    sub: "Crée un compte pour enregistrer tes configurations de gilet et les retrouver plus tard.",
  },
  default: { title: "Crée ton compte", sub: "Connecte-toi pour continuer." },
} as const;

export function AuthPromptModal() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<AuthPromptReason>(null);
  const pathname = usePathname();

  useEffect(
    () =>
      onAuthPrompt((r) => {
        setReason(r);
        setOpen(true);
      }),
    [],
  );

  // Renoncer : on ferme ET on oublie l'intention (cf. en-tête).
  const dismiss = useCallback(() => {
    clearPendingFavorite();
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") dismiss();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  const msg = MESSAGES[reason ?? "default"] ?? MESSAGES.default;
  const next = pathname || "/";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999] grid place-items-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={VEIL}
        >
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={dismiss}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={VEIL}
          />
          <motion.div
            className="relative w-full max-w-sm rounded-2xl border border-border bg-surface p-6 shadow-2xl"
            initial={{ opacity: 0, scale: 0.92, y: 22 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={CARD_IN}
            style={{ willChange: "transform" }}
          >
            <button
              onClick={dismiss}
              aria-label={t("Fermer")}
              className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-fg"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>

            {/* Pastille et titre CÔTE À CÔTE : le titre sous une pastille seule laissait un grand
                vide à sa droite. La pastille pulse une fois à l'ouverture — c'est le geste qu'on
                vient de faire (un ♥ cliqué) qui se rejoue. */}
            <motion.div
              className="flex items-center gap-3.5 pr-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={step(0)}
            >
              <motion.span
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent/15 text-accent"
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", bounce: 0.4, visualDuration: 0.5, delay: 0.16 }}
                style={{ willChange: "transform" }}
              >
                {/* Cœur SYMÉTRIQUE : l'ancien tracé montait à x≈18,5 à droite contre x≈1,9 à
                    gauche — le lobe droit paraissait rongé (Jules, 2026-08-24). */}
                <svg viewBox="0 0 24 24" className="h-[22px] w-[22px]" fill="currentColor" aria-hidden>
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
              </motion.span>
              <h2 className="font-display text-lg font-bold leading-tight">{t(msg.title)}</h2>
            </motion.div>

            <motion.p
              className="mt-3 text-sm text-fg-dim"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={step(1)}
            >
              {t(msg.sub)}
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={step(2)}
              onClick={() => void signIn.social({ provider: "google", callbackURL: next })}
              className="mt-5 flex w-full items-center justify-center gap-3 rounded-lg border border-border bg-bg py-2.5 text-sm font-medium text-fg transition-colors hover:border-border-strong hover:bg-surface-2"
            >
              <GoogleIcon /> {t("Continuer avec Google")}
            </motion.button>

            <motion.div
              className="my-3 flex items-center gap-3 text-xs text-muted"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={step(3)}
            >
              <span className="h-px flex-1 bg-border" /> {t("ou")} <span className="h-px flex-1 bg-border" />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={step(4)}>
              <Link
                href={`/connexion?next=${encodeURIComponent(next)}`}
                onClick={() => setOpen(false)}
                className="flex w-full items-center justify-center rounded-lg bg-accent py-2.5 text-sm font-semibold text-on-accent transition-colors hover:bg-accent-hover"
              >
                {t("Créer un compte / se connecter par email")}
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}
